import { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Popover,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AgGridReact } from "ag-grid-react";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { CommentsColumn } from "@/controls/ag-grid/components/CommentsColumn";
import type { ColDef } from "ag-grid-enterprise";
import { useStrandMoldHistoryQuery } from "../hooks/queries";
import { getTaskMetadata } from "../repositories/CasterMoldRepository";
import { useTaskMetadataQuery } from "../../../shared/hooks/queries";
import type { StrandMoldHistoryEntry } from "../models";

// =============================================================================
// Props
// =============================================================================

interface Props {
  strandId: number;
  /** Display name for the strand (e.g., "Strand 1") derived from tree data by the parent */
  strandName: string;
}

// =============================================================================
// Column Definitions
// =============================================================================

const CELL_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// =============================================================================
// Component
// =============================================================================

export const StrandMoldHistoryPanel = ({ strandId, strandName }: Props) => {
  const theme = useTheme();

  // --- Notes popover state ---
  const [notesPopoverState, setNotesPopoverState] = useState<{
    open: boolean;
    position: { top: number; left: number } | null;
    taskId: number | null;
  }>({ open: false, position: null, taskId: null });

  // --- Data ---
  const { entries, isLoading } = useStrandMoldHistoryQuery(strandId);

  const taskMetadataQuery = useTaskMetadataQuery(
    notesPopoverState.taskId,
    getTaskMetadata,
  );

  const metadata = taskMetadataQuery.data ?? [];
  const isLoadingNotes = taskMetadataQuery.isLoading;

  const handleShowNotes = (
    show: boolean,
    entry?: StrandMoldHistoryEntry,
    position?: { top: number; left: number },
  ) => {
    if (show && entry && position) {
      setNotesPopoverState({
        open: true,
        position,
        taskId: entry.TaskId,
      });
    } else {
      setNotesPopoverState({ open: false, position: null, taskId: null });
    }
  };

  // --- Column Defs ---
  const columnDefs: ColDef<StrandMoldHistoryEntry>[] = useMemo(
    () => [
      {
        field: "Date",
        headerName: "Date",
        minWidth: 140,
        cellStyle: CELL_STYLE,
        sort: "desc",
        valueFormatter: (params) => {
          if (!params.value) return "-";
          return dxtToLocalServerTime(params.value, "MM/dd/yyyy HH:mm");
        },
      },
      {
        field: "MoldName",
        headerName: "Mold",
        flex: 1,
        minWidth: 120,
        cellStyle: CELL_STYLE,
        valueFormatter: (params) => params.value ?? "-",
      },
      {
        field: "Action",
        headerName: "Action",
        width: 110,
        cellStyle: CELL_STYLE,
      },
      {
        field: "Heats",
        headerName: "Heats",
        width: 80,
        cellStyle: CELL_STYLE,
        valueFormatter: (params) => params.value?.toLocaleString() ?? "0",
      },
      {
        field: "Notes",
        headerName: "Notes",
        width: 70,
        cellStyle: CELL_STYLE,
        cellRenderer: (params: { data: StrandMoldHistoryEntry }) => {
          if (!params.data?.Notes) return null;
          return (
            <CommentsColumn
              data={{
                Comments: params.data.Notes,
                ElementId: params.data.TaskId,
              }}
              setShowOpenModal={(
                show: boolean,
                _data?: { Comments: string; ElementId: number },
                position?: { top: number; left: number },
              ) => {
                if (show) {
                  handleShowNotes(true, params.data, position);
                }
              }}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Paper
      elevation={2}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Mold History - {strandName}
        </Typography>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
          }}
        >
          <AgGridReact<StrandMoldHistoryEntry>
            rowData={entries}
            columnDefs={columnDefs}
            defaultColDef={{ ...defaultColDef }}
            loadingOverlayComponent={CenteredLazyLoading}
            loading={isLoading}
            {...defaultGridOptions}
          />
        </Box>
      </Box>

      {/* Notes Popover */}
      <Popover
        open={notesPopoverState.open}
        onClose={() => handleShowNotes(false)}
        anchorReference="anchorPosition"
        anchorPosition={notesPopoverState.position ?? undefined}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              padding: 0,
              overflow: "hidden",
              maxWidth: 400,
              maxHeight: 300,
            },
          },
        }}
      >
        <Box
          sx={{
            width: 400,
            maxHeight: 300,
            overflowY: "auto",
            p: 1.5,
            backgroundColor: "grey.50",
          }}
        >
          {isLoadingNotes ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : metadata.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No comments
            </Typography>
          ) : (
            metadata.map((entry) => (
              <Box
                key={entry.TaskMetadataId}
                sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                >
                  {entry.CreatedBy || "Unknown"},{" "}
                  {entry.CreatedAt
                    ? dxtToLocalServerTime(entry.CreatedAt, "MM/dd/yyyy HH:mm")
                    : "-"}
                  {entry.OperatorNumber != null &&
                    ` (Op: ${entry.OperatorNumber})`}
                </Typography>
                <Typography variant="body2">{entry.Notes}</Typography>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </Paper>
  );
};
